import { useCallback, useMemo, useRef } from 'react';
import { findAdjacentTabbable } from '../utils/findAdjacentTabbable.js';

/**
 * A11-003 (keyboard-only donor journey) / EMB-012 (keyboard reaches every
 * embedded-form control) — give a Tab back when a drawer's focus trap steals it.
 *
 * Spread the returned handlers on a vaul `Drawer.Content` (or any Radix
 * FocusScope container). Every drawer that holds a form must use this; it is
 * the one implementation, shared by `ResponsiveDrawer` and the widget overlay's
 * drawer.
 *
 * Why: Radix's FocusScope focuses its container whenever a node inside it is
 * removed while focus is on <body>. A native Tab passes through <body> between
 * the field it leaves and the one it enters, so a widget that tidies its DOM on
 * blur turns the donor's Tab into "focus the whole drawer": Mapbox clears its
 * aria-live text, react-select removes its menu and live-region text.
 *
 * So, for one task after a Tab keydown or a focusout, remember where focus was
 * headed: the focusout's `relatedTarget` when the browser already knew it,
 * otherwise the next tabbable in DOM order from the field the Tab left. If the
 * container itself takes focus inside that window, send focus on to that
 * target. A container focus with no Tab or blur in the same task — a lazy chunk
 * mounting, say — is left alone.
 *
 * @returns {{
 *   onKeyDownCapture: (event: KeyboardEvent) => void,
 *   onBlurCapture: (event: FocusEvent) => void,
 *   onFocusCapture: (event: FocusEvent) => void,
 * }}
 */
export function useDrawerTabHandoff() {
  const pendingFocusMoveRef = useRef(null);

  const rememberFocusMove = useCallback((move) => {
    pendingFocusMoveRef.current = { ...pendingFocusMoveRef.current, ...move };
    setTimeout(() => {
      pendingFocusMoveRef.current = null;
    }, 0);
  }, []);

  return useMemo(
    () => ({
      onKeyDownCapture: (event) => {
        if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
        rememberFocusMove({ from: event.target, backwards: event.shiftKey });
      },
      onBlurCapture: (event) => {
        // The container itself is where the steal sends focus, never a target.
        const next = event.relatedTarget;
        if (next && next !== event.currentTarget && event.currentTarget.contains(next)) {
          rememberFocusMove({ to: next });
        }
      },
      onFocusCapture: (event) => {
        const pending = pendingFocusMoveRef.current;
        if (!pending || event.target !== event.currentTarget) return;
        pendingFocusMoveRef.current = null;
        const container = event.currentTarget;
        const target =
          pending.to?.isConnected && container.contains(pending.to)
            ? pending.to
            : pending.from && container.contains(pending.from)
              ? findAdjacentTabbable(pending.from, {
                  backwards: pending.backwards,
                  scope: container.closest('[data-cs-dialog-portal-root]') || container,
                })
              : null;
        if (target && target !== container) target.focus({ preventScroll: true });
      },
    }),
    [rememberFocusMove],
  );
}
